import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import ReactTooltip from "react-tooltip";
import ReCAPTCHA from "react-google-recaptcha";
import { useLogin } from "../../queryHooks";
import { useStore } from "../../zustand/store";
import { loginSchema } from "../../validations";
import { CONFIG } from "../../config/configs.js";
import CS from "../../services/commonService";
import { URL } from "../../constants/URL";
import Theme from "../common/theme";

//	Component starts
const Login = () => {
	//	Navigation
	const navigate = useNavigate();
	const { mutateAsync: login, error: errorLogin, data: dataLogin } = useLogin();
	//	State values
	const [showPassword, setShowPassword] = useState(true);
	const [rememberMe, setRememberMe] = useState(false);
	const reCaptchaRef = useRef();
	const setLoading = useStore((state) => state.setLoading);

	//Defining state object for formik
	const [formData, setFormData] = useState({
		emailId: "",
		password: "",
	});

	/******************************************************************************************
	@Purpose    :   To initialize login query
	*******************************************************************************************/
	const { values, errors, handleChange, handleSubmit, touched } = useFormik({
		initialValues: formData,
		validationSchema: loginSchema,
		enableReinitialize: true,
		onSubmit: async (values) => {
			// const captchaToken = await reCaptchaRef.current.executeAsync();
			// reCaptchaRef.current.reset();
			// if (!captchaToken) {
			// 	CS.showErrorMessage("Captcha error, please try again.");
			// 	return;
			// }
			const reqObjNew = {
				email: values.emailId,
				password: values.password,
				captcha_token: "captchaToken",
			};
			login(reqObjNew);
			setLoading(true);
		},
	});

	useEffect(() => {
		const localStorageRememberMe = CS.getItem("rememberMe");
		if (localStorageRememberMe === "true") {
			let encryptedEmail = CS.getItem("emailId");
			let encryptedPassword = CS.getItem("password");
			const localStorageEmail = CS.decryptData(encryptedEmail);
			const localStoragePassword = CS.decryptData(encryptedPassword);
			setFormData({
				emailId: localStorageEmail,
				password: localStoragePassword,
			});
			setRememberMe(true);
		}
	}, []);

	/******************************************************************************************
	@Purpose    :   Response data of login api
	*******************************************************************************************/

	useEffect(() => {
		if (dataLogin) {
			setLoading(false);
			if (dataLogin.status === "success") {
				if (rememberMe) {
					CS.setItem("rememberMe", rememberMe);
					let encryptedEmail = CS.encryptData(values.emailId);
					let encryptedPassword = CS.encryptData(values.password);
					CS.setItem("emailId", encryptedEmail);
					CS.setItem("password", encryptedPassword);
				} else {
					CS.removeItem("rememberMe");
					CS.removeItem("emailId");
					CS.removeItem("password");
				}
				CS.setItem("token", dataLogin.data?.access_token);
				CS.setItem("userData", JSON.stringify(dataLogin.data?.user_details));
				navigate(URL.dashboard, { replace: true });
			} else if (dataLogin.status === "fail") {
				CS.showErrorMessage(dataLogin.message);
			}
		}
	}, [dataLogin]);

	/******************************************************************************************
	@Purpose    :   Response error of login api
	*******************************************************************************************/

	useEffect(() => {
		if (errorLogin) {
			setLoading(false);
			CS.showErrorMessage(errorLogin.response?.data?.message);
			navigate(URL.login, { replace: true });
		}
	}, [errorLogin]);

	/******************************************************************************************
	@Purpose    :   To show the tooltip for password show/hid
	*******************************************************************************************/
	const renderTooltip = () =>
		showPassword ? "Show Password" : "Hide Password";

	return (
		<>
			<Theme />
			<ReCAPTCHA
				size="invisible"
				sitekey={CONFIG.RECAPTCHA_SITE_KEY_PUBLIC}
				ref={reCaptchaRef}
			/>
			<div
				className="login-wrapper"
				style={{
					background:
						"url(../assets/images/login-bg.jpg) no-repeat center center",
				}}
			>
				<div className="login-body">
					<div className="login-container row m-0">
						<div className="col-xl-8 col-11">
							<div className="card bg-login-card">
								<div className="row m-0">
									<div className="col-md-6 col-12 pl-0 pr-0">
										<div className="card rounded-left mb-0 p-4 d-flex justify-content-center h-100">
											<div className="card-header">
												<div className="card-title text-center mb-5">
													<h4 className="mb-3">Welcome to OpaLink Admin</h4>
												</div>
											</div>
											<div className="sign-account">
												<p>Sign in to your account</p>
											</div>
											<div className="card-content">
												<div className="card-body">
													<form onSubmit={handleSubmit}>
														<div className="form-group mb-4">
															<label htmlFor="emailId">
																Email<sup className="text-danger">*</sup>
															</label>
															<input
																type="text"
																id="emailId"
																name="emailId"
																placeholder="Email"
																className={
																	errors.emailId
																		? "form-control input-error"
																		: "form-control"
																}
																value={values.emailId}
																onChange={handleChange}
															/>
															<span className="text-danger error-msg">
																{errors.emailId && touched.emailId
																	? errors.emailId
																	: null}
															</span>
														</div>
														<div className="form-group mb-4 position-relative password">
															<label htmlFor="password">
																Password<sup className="text-danger">*</sup>
															</label>
															<input
																className={
																	errors.password
																		? "form-control input-error"
																		: "form-control"
																}
																name="password"
																value={values.password}
																id="password"
																type={showPassword ? "password" : "text"}
																placeholder="Password"
																onChange={handleChange}
															/>
															<span
																onClick={() => setShowPassword(!showPassword)}
																data-tip
																data-for="registerTip"
																className={
																	showPassword
																		? "bx bx-hide field-icon toggle-password"
																		: "bx bx-show field-icon toggle-password"
																}
															></span>
															<ReactTooltip
																id="registerTip"
																place="top"
																effect="solid"
															>
																{renderTooltip()}
															</ReactTooltip>
															<em className="fa fa-lock" aria-hidden="true" />
															<span className="text-danger error-msg">
																{errors.password && touched.password
																	? errors.password
																	: null}
															</span>
														</div>
														<div className="form-group d-flex flex-md-row flex-column justify-content-between align-items-center mb-3 mt-3">
															<div className="text-left">
																<div className="form-group form-check form-check-small mb-0">
																	<input
																		type="checkbox"
																		className="form-check-input"
																		id="keepLogged"
																		checked={rememberMe}
																		onChange={() => setRememberMe(!rememberMe)}
																	/>
																	<label
																		className="form-check-label"
																		htmlFor="keepLogged"
																	>
																		Remember Me
																	</label>
																</div>
															</div>
															<div className="text-right">
																<Link
																	className="card-link"
																	to={{
																		pathname: URL.forgotPassword,
																		state: { emailId: values.emailId },
																	}}
																>
																	Forgot Password?
																</Link>
															</div>
														</div>
														<button
															className="btn btn-primary glow position-relative btn-block"
															type="submit"
														>
															Login
														</button>
													</form>
												</div>
											</div>
										</div>
									</div>
									<div className="col-md-6 d-md-block d-none text-center align-self-center p-7">
										<div className="card-content">
											<img
												className="img-fluid"
												src="../assets/images/login.png"
												alt="login"
											></img>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default Login;
